import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import ArticleIcon from '@mui/icons-material/ArticleOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTreeOutlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import SearchIcon from '@mui/icons-material/TravelExploreOutlined';
import PermMediaIcon from '@mui/icons-material/PermMediaOutlined';
import DesignServicesIcon from '@mui/icons-material/DesignServicesOutlined';
import FactoryIcon from '@mui/icons-material/FactoryOutlined';
import LightbulbIcon from '@mui/icons-material/LightbulbOutlined';
import WorkspacesIcon from '@mui/icons-material/WorkspacesOutlined';
import CollectionsIcon from '@mui/icons-material/CollectionsOutlined';
import SellIcon from '@mui/icons-material/SellOutlined';
import FormatQuoteIcon from '@mui/icons-material/FormatQuoteOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import RssFeedIcon from '@mui/icons-material/RssFeedOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBookOutlined';
import BuildIcon from '@mui/icons-material/BuildOutlined';
import QuizIcon from '@mui/icons-material/QuizOutlined';
import WorkIcon from '@mui/icons-material/WorkOutlineOutlined';
import DynamicFormIcon from '@mui/icons-material/DynamicFormOutlined';
import MoveToInboxIcon from '@mui/icons-material/MoveToInboxOutlined';
import LeaderboardIcon from '@mui/icons-material/LeaderboardOutlined';
import HubIcon from '@mui/icons-material/HubOutlined';
import BoltIcon from '@mui/icons-material/BoltOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import CampaignIcon from '@mui/icons-material/CampaignOutlined';
import EventIcon from '@mui/icons-material/EventAvailableOutlined';
import StarIcon from '@mui/icons-material/StarBorderOutlined';
import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutlined';
import BadgeIcon from '@mui/icons-material/BadgeOutlined';
import VpnKeyIcon from '@mui/icons-material/VpnKeyOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import ExtensionIcon from '@mui/icons-material/ExtensionOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import { paths } from '@/routes/paths';
import type { PermissionKey } from '@/types/permissions';
import type { PermissionMode } from '@/auth/permissions';

export type NavIcon = ComponentType<SvgIconProps>;

export interface NavItem {
  label: string;
  path: string;
  icon: NavIcon;
  /** Permission(s) required for this item to appear (backend still enforces). */
  permission?: PermissionKey | PermissionKey[];
  permissionMode?: PermissionMode;
  /** Phase in which the destination becomes fully functional (docs only). */
  phase?: number;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAVIGATION: NavGroup[] = [
  {
    label: 'Overview',
    items: [{ label: 'Dashboard', path: paths.dashboard, icon: DashboardIcon, phase: 1 }],
  },
  {
    label: 'Website',
    items: [
      { label: 'Pages', path: paths.pages, icon: ArticleIcon, permission: 'pages:view', phase: 3 },
      {
        label: 'Navigation',
        path: paths.navigation,
        icon: AccountTreeIcon,
        permission: 'navigation:view',
        phase: 3,
      },
      {
        label: 'Website Settings',
        path: paths.websiteSettings,
        icon: TuneIcon,
        permission: 'website-settings:view',
        phase: 3,
      },
      { label: 'SEO', path: paths.seo, icon: SearchIcon, permission: 'seo:view', phase: 3 },
      {
        label: 'Media',
        path: paths.media,
        icon: PermMediaIcon,
        permission: 'media:view',
        phase: 2,
      },
    ],
  },
  {
    label: 'Content',
    items: [
      {
        label: 'Services',
        path: paths.services,
        icon: DesignServicesIcon,
        permission: 'services:view',
        phase: 4,
      },
      {
        label: 'Industries',
        path: paths.industries,
        icon: FactoryIcon,
        permission: 'industries:view',
        phase: 4,
      },
      {
        label: 'Solutions',
        path: paths.solutions,
        icon: LightbulbIcon,
        permission: 'solutions:view',
        phase: 4,
      },
      {
        label: 'Case Studies',
        path: paths.caseStudies,
        icon: WorkspacesIcon,
        permission: 'case-studies:view',
        phase: 4,
      },
      {
        label: 'Portfolio',
        path: paths.portfolio,
        icon: CollectionsIcon,
        permission: 'portfolio:view',
        phase: 4,
      },
      {
        label: 'Pricing',
        path: paths.pricing,
        icon: SellIcon,
        permission: 'pricing:view',
        phase: 4,
      },
      {
        label: 'Testimonials',
        path: paths.testimonials,
        icon: FormatQuoteIcon,
        permission: 'testimonials:view',
        phase: 4,
      },
      { label: 'Team', path: paths.team, icon: GroupsIcon, permission: 'team:view', phase: 4 },
      { label: 'Blog', path: paths.blog, icon: RssFeedIcon, permission: 'blog:view', phase: 5 },
      {
        label: 'Resources',
        path: paths.resources,
        icon: MenuBookIcon,
        permission: 'resources:view',
        phase: 5,
      },
      {
        label: 'Free Tools',
        path: paths.tools,
        icon: BuildIcon,
        permission: 'tools:view',
        phase: 5,
      },
      { label: 'FAQs', path: paths.faqs, icon: QuizIcon, permission: 'faqs:view', phase: 5 },
    ],
  },
  {
    label: 'Acquisition',
    items: [
      {
        label: 'Leads',
        path: paths.leads,
        icon: LeaderboardIcon,
        permission: 'leads:view',
        phase: 6,
      },
      { label: 'CRM', path: paths.crm, icon: HubIcon, permission: 'crm:view', phase: 6 },
      {
        label: 'Forms',
        path: paths.forms,
        icon: DynamicFormIcon,
        permission: 'forms:view',
        phase: 5,
      },
      {
        label: 'Submissions',
        path: paths.submissions,
        icon: MoveToInboxIcon,
        permission: 'form-submissions:view',
        phase: 5,
      },
      {
        label: 'Automations',
        path: paths.automations,
        icon: BoltIcon,
        permission: 'automation:view',
        phase: 7,
      },
      {
        label: 'Email Templates',
        path: paths.emailTemplates,
        icon: EmailIcon,
        permission: 'email-templates:view',
        phase: 7,
      },
      {
        label: 'Popups',
        path: paths.popups,
        icon: CampaignIcon,
        permission: 'popups:view',
        phase: 7,
      },
      {
        label: 'Booking',
        path: paths.booking,
        icon: EventIcon,
        permission: 'booking:view',
        phase: 7,
      },
      {
        label: 'Reviews',
        path: paths.reviews,
        icon: StarIcon,
        permission: 'reviews:view',
        phase: 4,
      },
      {
        label: 'Careers',
        path: paths.careers,
        icon: WorkIcon,
        permission: 'careers:view',
        phase: 5,
      },
    ],
  },
  {
    label: 'Insights',
    items: [
      {
        label: 'Analytics',
        path: paths.analytics,
        icon: InsightsIcon,
        permission: 'analytics:view',
        phase: 7,
      },
    ],
  },
  {
    label: 'Administration',
    items: [
      { label: 'Users', path: paths.users, icon: PeopleIcon, permission: 'users:view', phase: 8 },
      { label: 'Roles', path: paths.roles, icon: BadgeIcon, permission: 'roles:view', phase: 8 },
      {
        label: 'Permissions',
        path: paths.permissions,
        icon: VpnKeyIcon,
        permission: 'permissions:view',
        phase: 8,
      },
      {
        label: 'Audit Logs',
        path: paths.auditLogs,
        icon: HistoryIcon,
        permission: 'audit:view',
        phase: 8,
      },
      {
        label: 'Integrations',
        path: paths.integrations,
        icon: ExtensionIcon,
        permission: 'integrations:view',
        phase: 8,
      },
      {
        label: 'System Settings',
        path: paths.systemSettings,
        icon: SettingsIcon,
        permission: 'settings:view',
        phase: 8,
      },
    ],
  },
];

/** Flat list of all nav items (for command palette / breadcrumb lookups). */
export const NAV_ITEMS_FLAT: NavItem[] = NAVIGATION.flatMap((g) => g.items);
